import React, { useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { MyContext } from "../../context/MyContext";
import Menu from "../../components/menu/Menu";
import { Mode } from "../tutorV2/types";
import DashboardCount from "../../components/dashboard/Count";
import DashboardSuggestion from "../../components/dashboard/Suggestion";
import DashboardChartLine from "../../components/dashboard/ChartLine";
import DashboardChartKeyword from "../../components/dashboard/ChartKeyword";
import CardResult from "../../components/card/Result";
import LoginModal from "../chat/modal/LoginModal";
import Loading from "../../components/Loading";
import useMyActivityView from "./hooks/useMyActivityView";
import { Back } from "../../assets/svgs/icons";
import { LogoDefault } from "../../assets/svgs/logos";
import AirHeader from "../../components/AirHeader";

const MyActivityV2: React.FC = () => {
  const { user, isMobile } = useContext(MyContext);
  const isLoggedIn = !!user?.id;

  const {
    loading,
    curiosity,
    activityTime,
    topKeywords,
    recommendKeywords,
    socratesResults,
    socratesTotal,
    handleViewResult,
    handleGoBack,
    isLogin,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyActivityView();

  // 스크롤 감지를 위한 Intersection Observer
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
  });

  // 스크롤이 하단에 도달하면 다음 페이지 로드
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex flex-row h-full w-full bg-white overflow-hidden">
      {/* Sidebar */}
      {isLoggedIn && <Menu mode={Mode.TUTOR} />}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
        {/* Header - 모바일: AirHeader, PC: 자체 헤더 */}
        {isMobile ? (
          <AirHeader type="sub" title="나의 활동" />
        ) : (
          <header className="relative flex items-center justify-center bg-white w-full h-[64px] shrink-0">
            {/* Back Button */}
            <button
              onClick={handleGoBack}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-4"
              aria-label="뒤로가기"
            >
              <Back className="w-6 h-6 text-[#212121]" />
            </button>

            {/* Logo */}
            <LogoDefault className="w-[100px] h-[25px]" />
          </header>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-8">
          <div className="flex flex-col gap-8 max-w-[800px] mx-auto w-full">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loading />
              </div>
            ) : (
              <>
                {/* Title - PC에서만 표시 */}
                <div className="hidden md:flex flex-col gap-2">
                  <h1 className="font-bold text-[24px] leading-[36px] text-[#212121]">
                    나의 활동
                  </h1>
                </div>

                {/* Section 1: 호기심 지수 + 키워드 추천 (PC) / 호기심 지수만 (MO) */}
                <div className="flex flex-wrap gap-6 items-stretch">
                  {/* 호기심 지수 */}
                  <div className="flex-1 min-w-[328px] flex flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      호기심 지수
                    </h2>
                    <DashboardCount
                      tutorCount={curiosity.tutorCount}
                      avgQuestionsPerTutor={curiosity.avgQuestionsPerTutor}
                      socratesCount={curiosity.socratesCount}
                      className="flex-1"
                    />
                  </div>

                  {/* 키워드 추천 - PC에서만 여기 표시, MO에서는 숨김 */}
                  <div className="hidden md:flex flex-1 min-w-[328px] flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      키워드 추천
                    </h2>
                    <DashboardSuggestion
                      description="비슷한 관심키워드를 가진 사람들은"
                      percentageBold={`사용자의 ${
                        recommendKeywords.percentage ?? 0
                      }%`}
                      percentageNormal="에요"
                      cardText="사람들은 이런 키워드에도 관심을 가졌어요!"
                      keywords={
                        recommendKeywords.keywords.length > 0
                          ? recommendKeywords.keywords
                          : ["주제", "오늘", "과학지식"]
                      }
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Section 2 (MO only): 호기심이 가장 발휘되는 순간 */}
                <div className="flex md:hidden flex-wrap gap-6 items-stretch">
                  <div className="flex-1 min-w-[328px] flex flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      호기심이 가장 발휘되는 순간
                    </h2>
                    <DashboardChartLine
                      data={activityTime.dayList}
                      topDate={activityTime.topDate}
                    />
                  </div>
                </div>

                {/* Section 3 (MO only): 키워드 추천 */}
                <div className="flex md:hidden flex-wrap gap-6 items-stretch">
                  <div className="flex-1 min-w-[328px] flex flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      키워드 추천
                    </h2>
                    <DashboardSuggestion
                      description="비슷한 관심키워드를 가진 사람들은"
                      percentageBold={`사용자의 ${
                        recommendKeywords.percentage ?? 0
                      }%`}
                      percentageNormal="에요"
                      cardText="사람들은 이런 키워드에도 관심을 가졌어요!"
                      keywords={
                        recommendKeywords.keywords.length > 0
                          ? recommendKeywords.keywords
                          : ["주제", "오늘", "과학지식"]
                      }
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Section 4: 호기심이 가장 발휘되는 순간 + 관심키워드 TOP5 (PC) / 관심키워드 TOP5만 (MO) */}
                <div className="flex flex-wrap gap-6 items-stretch">
                  {/* 호기심이 가장 발휘되는 순간 - PC에서만 여기 표시, MO에서는 숨김 */}
                  <div className="hidden md:flex flex-1 min-w-[328px] flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      호기심이 가장 발휘되는 순간
                    </h2>
                    <DashboardChartLine
                      data={activityTime.dayList}
                      topDate={activityTime.topDate}
                    />
                  </div>

                  {/* 관심키워드 TOP5 */}
                  <div className="flex-1 min-w-[328px] flex flex-col gap-3">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      관심키워드 TOP5
                    </h2>
                    <DashboardChartKeyword keywords={topKeywords} />
                  </div>
                </div>

                {/* Section 3: 소크라테스 모의 면접 결과 */}
                <div className="border-t border-[#eeeeee] pt-6 flex flex-col gap-3">
                  <div className="flex justify-between items-center">
                    <h2 className="font-bold text-[16px] leading-[24px] tracking-[-0.32px] text-[#212121]">
                      소크라테스 모의 면접 결과
                    </h2>
                    <span className="font-medium text-[14px] leading-[16px] tracking-[-0.28px] text-[#888888]">
                      {socratesTotal}건
                    </span>
                  </div>

                  {socratesResults.length > 0 ? (
                    <>
                      <div className="flex flex-wrap gap-3">
                        {socratesResults.map((result) => (
                          <CardResult
                            key={result.id}
                            title={result.title}
                            interviewType={result.interviewType}
                            grade={result.grade}
                            interviewDate={result.interviewDate}
                            onViewResult={() => handleViewResult(result.id)}
                            className="w-[calc(50%-6px)] min-w-[280px] max-md:w-full"
                          />
                        ))}
                      </div>

                      {/* 스크롤 감지용 div */}
                      <div ref={loadMoreRef} className="h-1" />

                      {/* 로딩 인디케이터 */}
                      {isFetchingNextPage && (
                        <div className="flex justify-center py-4">
                          <Loading />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-[#888888]">
                      면접 내역이 없습니다.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {isLogin && <LoginModal />}
    </div>
  );
};

export default MyActivityV2;
